import { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import MetricCard from "../components/ui/MetricCard";
import Table, { Badge } from "../components/ui/Table";
import type { Metric } from "../types";
import { fetchWithAdminAuth } from "../utils/adminAuth";

export default function Dashboard() {
  const [sessionData, setSessionData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalConversationsToday: 0,
    totalRevenue: 0,
    totalRevenueNoIVA: 0,
    estimatedAICost: 0,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDuration = (seconds: number) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const costPercentage = dashboardMetrics.totalRevenueNoIVA > 0
    ? ((dashboardMetrics.estimatedAICost / dashboardMetrics.totalRevenueNoIVA) * 100).toFixed(1)
    : '0';

  const metrics: Metric[] = [
    {
      label: "Consultas IA hoje",
      value: dashboardMetrics.totalConversationsToday,
      footer: { left: "", right: "Ver sessões" },
    },
    {
      label: "Faturação total (com IVA)",
      value: formatCurrency(dashboardMetrics.totalRevenue),
      footer: { left: "", right: "Ver detalhes" },
    },
    {
      label: "Faturação total (sem IVA)",
      value: formatCurrency(dashboardMetrics.totalRevenueNoIVA),
      footer: { left: "", right: "Ver detalhes" },
    },
    {
      label: "Custo IA real hoje",
      value: `$${dashboardMetrics.estimatedAICost.toFixed(2)}`,
      change: { value: `${costPercentage}% da receita`, type: "down" },
      footer: { left: "Custo real (tokens OpenAI)", right: "Ver IA" },
    },
  ];

  const sessionColumns = [
    { header: "Cliente", key: "cliente" },
    { header: "Consultor", key: "consultor" },
    { header: "Início", key: "inicio" },
    { header: "Duração Max", key: "duracao" },
    {
      header: "Estado",
      key: "estado",
      render: (_val: string) => <Badge variant="success">Em curso</Badge>,
    },
  ];

  useEffect(() => {
    const loadDashboardMetrics = async () => {
      try {
        const response = await fetchWithAdminAuth("/api/admin/getDashboardMetrics", { method: "GET" });

        if (!response.ok) {
          throw new Error(`Failed to fetch metrics: ${response.statusText}`);
        }

        const data = await response.json();
        setDashboardMetrics({
          totalConversationsToday: Number(data?.totalConversationsToday ?? 0),
          totalRevenue: Number(data?.totalRevenue ?? 0),
          totalRevenueNoIVA: Number(data?.totalRevenueNoIVA ?? 0),
          estimatedAICost: Number(data?.estimatedAICost ?? 0),
        });
      } catch (error) {
        console.error("Error loading dashboard metrics:", error);
        setDashboardMetrics({
          totalConversationsToday: 0,
          totalRevenue: 0,
          totalRevenueNoIVA: 0,
          estimatedAICost: 0,
        });
      }
    };

    const loadActiveSessions = async () => {
      try {
        const response = await fetchWithAdminAuth("/api/admin/getActiveSessions", { method: "GET" });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }

        const data = await response.json();
        const sessions = Array.isArray(data?.activeConversations) ? data.activeConversations : [];
        const mappedSessions = sessions.map((session: any) => ({
          cliente: session?.user?.name || "—",
          consultor: session?.consultant?.name || "—",
          inicio: session?.startDate
            ? new Date(session.startDate).toLocaleString("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
          duracao: formatDuration(Number(session?.maxDuration || 0)),
          estado: "active",
        }));
        setSessionData(mappedSessions);
      } catch (error) {
        console.error("Error loading active sessions:", error);
        setSessionData([]);
      }
    };

    const loadDashboardData = async () => {
      try {
        await Promise.all([loadDashboardMetrics(), loadActiveSessions()]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  return loading ? (
    <div className="px-3 sm:px-6 py-4 sm:py-5 flex items-center justify-center">
      <span className="text-text-soft">Carregando dados do dashboard...</span>
    </div>
  ) : (
    <div className="px-3 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
      <Card
        title="Resumo de hoje"
        subtitle="Visão geral rápida de consultas humanas e IA, faturação e sessões em curso."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.map((metric, idx) => (
            <MetricCard key={idx} metric={metric} />
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)] gap-4">
        <Card
          title="Sessões em tempo real"
          subtitle="Consultas humanas e IA atualmente em curso."
        >
          <Table columns={sessionColumns} data={sessionData} />
        </Card>

        <Card
          title="Alertas rápidos"
          subtitle="Informação importante para hoje."
        >
          <ul className="list-none text-[0.8rem] text-text-soft flex flex-col gap-1.5">
            {/* <li>• Dois consultores com menos de 10 horas este mês.</li>
            <li>• 3 clientes com pagamentos pendentes IA.</li>
            <li>• Lembrar: atualizar Carta da Semana de domingo.</li> */}
          </ul>
        </Card>
      </div>
    </div>
  );
}
